import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, interpolate, spring, useVideoConfig } from "remotion";
import { Background } from "../components/Background";
import { AnimatedText } from "../components/AnimatedText";
import { SERIES_THEMES } from "../styles/theme";

// 10 Style components
import { MandalaGenesis } from "../components/styles/MandalaGenesis";
import { BreathingCircle } from "../components/styles/BreathingCircle";
import { FloatingParticles } from "../components/styles/FloatingParticles";
import { LightLeakCaustics } from "../components/styles/LightLeakCaustics";
import { SacredGeometryGrid } from "../components/styles/SacredGeometryGrid";
import { LotusBloom } from "../components/styles/LotusBloom";
import { PulsingGlow } from "../components/styles/PulsingGlow";
import { MinimalLines } from "../components/styles/MinimalLines";
import { FrequencyWaves } from "../components/styles/FrequencyWaves";
import { PortalVortex } from "../components/styles/PortalVortex";

export type StyleName =
  | "mandala" | "breathing" | "particles" | "caustics" | "geometry"
  | "lotus" | "glow" | "minimal" | "waves" | "vortex";

interface StyleTestProps {
  styleName: StyleName;
}

const STYLE_COMPONENTS: Record<StyleName, React.FC<{ opacity?: number }>> = {
  mandala: MandalaGenesis,
  breathing: BreathingCircle,
  particles: FloatingParticles,
  caustics: LightLeakCaustics,
  geometry: SacredGeometryGrid,
  lotus: LotusBloom,
  glow: PulsingGlow,
  minimal: MinimalLines,
  waves: FrequencyWaves,
  vortex: PortalVortex,
};

const STYLE_LABELS: Record<StyleName, string> = {
  mandala: "1. Mandala Genesis",
  breathing: "2. Breathing Circle",
  particles: "3. Floating Particles",
  caustics: "4. Light Leak & Caustics",
  geometry: "5. Sacred Geometry",
  lotus: "6. Lotus Bloom",
  glow: "7. Pulsing Glow",
  minimal: "8. Minimal Lines",
  waves: "9. Frequency Waves",
  vortex: "10. Portal Vortex",
};

// Composite reel test script from EP1 - Samadhi Pada 1
// Using real sentences from Gurudev's talk
const REEL_BEATS = [
  {
    label: "HOOK",
    cues: [
      { text: "When you say 'now', what happens?", start: 0, end: 4 },
      { text: "You suddenly become alert. Your mind is right there.", start: 4.5, end: 10 },
    ],
  },
  {
    label: "TEACHING",
    cues: [
      { text: "It's so amazing — describing the most indescribable truth in the universe.", start: 12, end: 20 },
      { text: "The most indescribable phenomenon called consciousness.", start: 20.5, end: 27 },
    ],
  },
  {
    label: "INSIGHT",
    cues: [
      { text: "The self is much more joyful, much more glorious, much more charming than any other charms.", start: 29, end: 39 },
      { text: "A little glimpse already reduces the cravings and brings you back to the self.", start: 39.5, end: 47 },
    ],
  },
  {
    label: "CLOSE",
    cues: [
      { text: "When you are in a sense of wow — it is not about the scenery, but about the seer.", start: 49, end: 57 },
    ],
  },
];

export const StyleTest: React.FC<StyleTestProps> = ({ styleName }) => {
  const { fps, width, height } = useVideoConfig();
  const frame = useCurrentFrame();
  const theme = SERIES_THEMES["patanjali-yoga-sutras-2019"];
  const StyleComponent = STYLE_COMPONENTS[styleName];
  const isPortrait = height > width;

  // Style label fade in/out
  const labelOpacity = interpolate(frame, [0, 20, 50, 70], [0, 1, 1, 0], {
    extrapolateRight: "clamp",
  });

  const fontSize = isPortrait ? 34 : 38;
  const padding = isPortrait ? "0 60px" : "0 200px";

  return (
    <AbsoluteFill>
      {/* Dark base background */}
      <AbsoluteFill style={{
        background: `radial-gradient(ellipse at 50% 40%, ${theme.gradientEnd} 0%, ${theme.gradientStart} 70%)`,
      }} />

      {/* Style layer */}
      <StyleComponent />

      {/* Vignette */}
      <AbsoluteFill style={{
        background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.5) 100%)",
      }} />

      {/* Style label (first 2.3 seconds) */}
      <Sequence from={0} durationInFrames={70}>
        <AbsoluteFill style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: labelOpacity,
        }}>
          <div style={{
            fontSize: isPortrait ? 28 : 32,
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 600,
            color: "#F37021",
            textTransform: "uppercase",
            letterSpacing: "0.15em",
            textAlign: "center",
          }}>
            {STYLE_LABELS[styleName]}
          </div>
        </AbsoluteFill>
      </Sequence>

      {/* Reel content */}
      {REEL_BEATS.map((beat, beatIdx) => {
        // Calculate beat start frame
        let beatStartSec = 0;
        for (let b = 0; b < beatIdx; b++) {
          const lastCue = REEL_BEATS[b].cues[REEL_BEATS[b].cues.length - 1];
          beatStartSec = lastCue.end + 2; // 2 second gap between beats
        }

        return beat.cues.map((cue, cueIdx) => {
          const cueStartFrame = Math.round((beatStartSec + cue.start) * fps);
          const cueDurationFrames = Math.round((cue.end - cue.start) * fps);

          return (
            <Sequence key={`${beatIdx}-${cueIdx}`} from={cueStartFrame} durationInFrames={cueDurationFrames}>
              <AbsoluteFill style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding,
              }}>
                {/* Beat label (subtle) */}
                {cueIdx === 0 && (
                  <div style={{
                    position: "absolute",
                    top: isPortrait ? 100 : 50,
                    left: isPortrait ? 40 : 60,
                    fontSize: 12,
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 500,
                    color: "#F37021",
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    opacity: 0.5,
                  }}>
                    {beat.label}
                  </div>
                )}
                <AnimatedText
                  text={cue.text}
                  startFrame={0}
                  durationFrames={cueDurationFrames}
                  style={beatIdx === 0 && cueIdx === 0 ? "fade-in" : "word-reveal"}
                  fontSize={beatIdx === 2 ? fontSize + 4 : fontSize}
                  accentColor={theme.accentColor}
                  textAlign="center"
                />
              </AbsoluteFill>
            </Sequence>
          );
        });
      })}

      {/* Series branding - bottom */}
      <div style={{
        position: "absolute",
        bottom: isPortrait ? 100 : 40,
        left: 0,
        right: 0,
        display: "flex",
        justifyContent: "center",
        opacity: 0.4,
      }}>
        <span style={{
          fontSize: 14,
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 400,
          color: "#D4A843",
          letterSpacing: "0.1em",
        }}>
          PATANJALI YOGA SUTRAS — EP 1
        </span>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
